import { useRef, useEffect, useState } from 'react';

// New product images
const COOKWARE_SET_WHITE_BG = "https://customer-assets.emergentagent.com/job_precision-cookware/artifacts/c3d3eban_Cookware%20Set.png";
const COOKING_POT_BLACK_BG = "https://customer-assets.emergentagent.com/job_precision-cookware/artifacts/ivueh0c0_Cooking%20pot.png";
const WOK_URL = "https://customer-assets.emergentagent.com/job_precision-cookware/artifacts/lm6dnv3j_Cooking%20wok.png";

export const ProductShowcase = () => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      const visibleTop = Math.max(0, viewportHeight - rect.top);
      const progress = Math.min(1, Math.max(0, visibleTop / viewportHeight));
      
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const products = [
    {
      image: COOKING_POT_BLACK_BG,
      title: "Casserole Pot",
      subtitle: "The heart of every kitchen",
      description: "SS316 interior, tri-ply construction, glass lid for perfect visibility."
    },
    {
      image: WOK_URL,
      title: "Professional Wok",
      subtitle: "High-heat performance",
      description: "Honeycomb non-stick surface, ergonomic handle, induction ready."
    }
  ];

  return (
    <section
      ref={sectionRef}
      data-testid="product-showcase-section"
      className="relative py-16 md:py-24 lg:py-32 bg-tex-bg overflow-hidden"
    >
      {/* Section Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 mb-12 md:mb-20">
        <p
          className="font-raleway text-[10px] font-semibold uppercase tracking-[0.5em] text-tex-violet-bright mb-4 md:mb-6"
          style={{
            opacity: Math.min(1, scrollProgress * 3),
            transform: `translateY(${Math.max(0, 20 - scrollProgress * 60)}px)`
          }}
        >
          Close-Up Details
        </p>
        <h2
          data-testid="showcase-heading"
          className="text-editorial-section text-white text-left"
          style={{
            opacity: Math.min(1, scrollProgress * 2.5),
            transform: `translateY(${Math.max(0, 30 - scrollProgress * 90)}px)`
          }}
        >
          Precision in
          <br className="hidden sm:block" />
          every detail.
        </h2>
      </div>

      {/* Product Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12">
          {products.map((product, index) => (
            <div
              key={product.title}
              data-testid={`product-closeup-${index + 1}`}
              className="group relative bg-black rounded-sm overflow-hidden"
              style={{
                opacity: Math.min(1, Math.max(0, (scrollProgress - 0.1 - index * 0.1) * 3)),
                transform: `translateY(${Math.max(0, 40 - (scrollProgress - 0.1 - index * 0.1) * 120)}px)`
              }}
            >
              {/* Image Container */}
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-contain transform group-hover:scale-105 transition-transform duration-700"
                />
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              
              {/* Product Info */}
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                <p className="font-raleway text-[10px] font-semibold uppercase tracking-[0.3em] text-tex-violet-bright mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                  {product.subtitle}
                </p>
                <h3 className="font-cormorant italic text-xl sm:text-2xl md:text-3xl text-white mb-2">
                  {product.title}
                </h3>
                <p className="font-raleway text-xs sm:text-sm font-light text-tex-muted leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200">
                  {product.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Full Width Feature Image */}
        <div 
          className="mt-8 md:mt-12 lg:mt-16 relative overflow-hidden rounded-sm"
          style={{
            opacity: Math.min(1, Math.max(0, (scrollProgress - 0.4) * 2.5)),
            transform: `translateY(${Math.max(0, 40 - (scrollProgress - 0.4) * 100)}px)`
          }}
        >
          <div className="relative aspect-[16/9] sm:aspect-[21/9] bg-gradient-to-br from-tex-surface to-black overflow-hidden">
            <div
              style={{
                maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%)',
                WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%)',
              }}
              className="w-full h-full"
            >
              <img
                src={COOKWARE_SET_WHITE_BG}
                alt="Tex-Style Company 8-Piece Cookware Set"
                data-testid="feature-image"
                className="w-full h-full object-contain opacity-90 hover:opacity-100 transition-all duration-700"
              />
            </div>
            {/* Overlay Content */}
            <div className="absolute inset-0 flex items-end p-4 sm:p-6 md:p-8 lg:p-12 bg-gradient-to-t from-black/70 via-transparent to-transparent">
              <div>
                <p className="font-raleway text-[10px] font-semibold uppercase tracking-[0.3em] text-tex-violet-bright mb-2">
                  Complete Collection
                </p>
                <h3 className="font-cormorant italic text-2xl sm:text-3xl md:text-4xl text-white mb-2 md:mb-3">
                  8-Piece Tri-Ply Set
                </h3>
                <p className="font-raleway text-xs sm:text-sm font-light text-tex-muted max-w-md">
                  Everything you need for a professional kitchen. SS316 interior throughout.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
